/**
 * Orientation controller (sc-2829).
 *
 * Detects "playable viewport" vs "unplayable viewport" and flips a body
 * attribute the CSS uses to show/hide the rotate-to-play overlay. The
 * cabinet is landscape-only by design AND requires a minimum size — so
 * we treat all three of these as "unplayable":
 *   - Portrait orientation (height > width)
 *   - Too-narrow landscape (width < 480px — below 1× internal-res scale)
 *   - Too-short landscape (height < 270px — below 1× internal-res scale)
 *
 * Players in any of those states get the rotate-to-play card. Otherwise
 * the cabinet renders.
 *
 * Detection uses `matchMedia` rather than the orientation API because
 * device orientation and viewport aspect ratio can disagree (e.g.,
 * a desktop browser window resized small isn't a "rotated device").
 * We care about the viewport, not the hardware.
 *
 * Lifecycle: `attachOrientationController()` installs the matchMedia
 * listener at cabinet boot. Returns a detach function for tests.
 */

// Minimum playable viewport. Below this the cabinet's chrome takes too
// large a fraction of the canvas and the game is unreadable.
const MIN_WIDTH = 480;
const MIN_HEIGHT = 320;

// Catches portrait OR too-narrow landscape OR too-short landscape.
// Anything matching this is "unplayable" and gets the rotate overlay.
const UNPLAYABLE_QUERY = `(orientation: portrait), (max-width: ${MIN_WIDTH - 1}px), (max-height: ${MIN_HEIGHT - 1}px)`;

export function attachOrientationController(
  win: Window = globalThis.window
): () => void {
  const mql = win.matchMedia(UNPLAYABLE_QUERY);

  const apply = (isUnplayable: boolean): void => {
    const body = win.document.body;
    // We keep the attribute name `data-orientation` for CSS compatibility,
    // but the semantic is now "playable" (landscape, big enough) vs
    // "unplayable" (portrait OR too small).
    body.dataset.orientation = isUnplayable ? 'portrait' : 'landscape';
    const overlay = win.document.getElementById('rotate-overlay');
    if (overlay) {
      overlay.setAttribute('aria-hidden', String(!isUnplayable));
    }
  };

  apply(mql.matches);

  const onChange = (e: MediaQueryListEvent): void => apply(e.matches);
  // Modern browsers: addEventListener; legacy Safari: addListener.
  if ('addEventListener' in mql) {
    mql.addEventListener('change', onChange);
  } else {
    (mql as unknown as { addListener(fn: (e: MediaQueryListEvent) => void): void }).addListener(
      onChange
    );
  }

  return () => {
    if ('removeEventListener' in mql) {
      mql.removeEventListener('change', onChange);
    } else {
      (
        mql as unknown as {
          removeListener(fn: (e: MediaQueryListEvent) => void): void;
        }
      ).removeListener(onChange);
    }
  };
}
